import axios from 'axios'
import { baseApiUrl,AuthApiUrl,MdfeApiUrl,CteEngineApiUrl,CadastroApiUrl, FinanceiroApiUrl, EntregasApiUrl, WiCiotApiUrl, NfseApiUrl} from '../config'
import { novoCorrelationId } from '../utils/correlationId'

// Guarda para evitar redirect/reload em multiplas requisicoes paralelas.
// Quando o token expira, varias chamadas pendentes voltam 401 ao mesmo
// tempo; sem isso, dispararia varios reloads.
let handling401 = false

/**
 * Interceptor global de erros de autenticacao.
 *
 * Antes: cada tela tratava 401 individualmente no .catch(). Algumas
 * (CadastroVeiculo, CiotCadastro, CadastroMotorista) nao tratavam,
 * deixando o usuario preso em "carregando" sem feedback.
 *
 * Agora: qualquer erro de auth em qualquer axios instance (exceto
 * httpAuth) limpa o storage e redireciona pro login automaticamente.
 *
 * Status considerados auth (ver isAuthError):
 *  - 401 Unauthorized (padrao)
 *  - 403 Forbidden (cheap defense)
 *  - 500 Internal Server Error QUANDO o usuario tem token salvo E o body
 *    bate com o formato de erro do backend ({mensagem, data, recurso}
 *    com mensagem null/vazia) OU contem palavras-chave de auth. Eh um
 *    workaround pra um bug do backend que retorna 500 em vez de 401
 *    quando o JWT eh invalido/expirado em alguns endpoints. Idealmente
 *    isso deveria ser corrigido no Spring Security do backend.
 *
 * Excecoes:
 * - Requisicoes via httpAuth (endpoint de login/autenticacao): evita
 *   loop quando o usuario erra a senha (backend responde 401).
 * - Requisicoes via axios direto (sem instancia): nao sao
 *   interceptadas. Inclui LoginService.renovarToken() de proposito -
 *   se a renovacao falha, o proximo request normal cuida do logout.
 * - Quando o usuario ja esta na tela de login.
 */
/**
 * Detecta erro de autenticacao = SOMENTE HTTP 401.
 *
 * Antes existia uma heuristica que tambem tratava 403 e ate 500 como
 * expiracao de sessao. Isso causava logout indevido:
 *  - 403: usuario sem permissao num endpoint era deslogado em vez de ver
 *    "acesso negado".
 *  - 500: erro de negocio era confundido com auth. Pior no wi-cadastro-api,
 *    que NAO exige token (todos os endpoints de motorista/participante sao
 *    abertos) e retorna 500 com { mensagem:null, recurso } em qualquer erro
 *    de runtime - o que deslogava o usuario ao salvar/carregar motorista.
 *
 * Os backends que de fato exigem auth ja retornam 401 corretamente quando o
 * token expira/invalida, entao basta tratar 401.
 */
function isAuthError(error) {
    return !!(error && error.response && error.response.status === 401)
}

function handle401(error) {
    if (isAuthError(error) && !handling401) {
        // Se ja estamos na tela de login (ex.: usuario errou senha), nao redireciona
        const hash = (typeof window !== 'undefined' && window.location) ? (window.location.hash || '') : ''
        const isOnLoginPage = hash.indexOf('login') !== -1

        if (!isOnLoginPage) {
            handling401 = true

            // Limpa dados de sessao do usuario (mesmo conjunto que Header.vue.logout)
            try {
                const authKeys = ['token', 'id', 'idUsuario', 'userLogin',
                                  'permissoes', 'username', 'userData',
                                  'tokenAtualizacao', 'tokenExpiration',
                                  // Carimbos do controle de sessao. Sem
                                  // limpar, a ociosidade da sessao antiga
                                  // sobrevive ao logout por 401 e o proximo
                                  // login ja nasceria perto do limite.
                                  'sessaoUltimaAtividade', 'sessaoInicio',
                                  // Rotina "Participante Ativo" - limpar
                                  // junto com o resto da sessao.
                                  'participanteAtivo']
                authKeys.forEach(function(k){ localStorage.removeItem(k) })
                // Modais que sao reexibidos no proximo login
                sessionStorage.removeItem('modais_exibidos')
            } catch (e) {
                // localStorage indisponivel
            }

            // Reload completo: re-inicializa o Vuex (state.user.isLoggedIn
            // releitura do localStorage agora vazio = false) e o router
            // beforeEach manda pro Login automaticamente.
            try {
                window.location.hash = '#/login'
                window.location.reload()
            } catch (e) {
                // SSR ou janela indisponivel
            }
        }
    }
    return Promise.reject(error)
}

/**
 * Interceptor da rotina "Participante Ativo" (ROLE_MODERADOR).
 *
 * Quando o moderador escolhe um participante no Painel Inicial, este
 * interceptor adiciona idParticipanteAtivo=<id> em TODAS as requisicoes
 * que saem das instances `http` (cte-app-web-api) e `httpCadastro`
 * (wi-cadastro-api). Backend ignora silenciosamente quando o param eh
 * null/ausente (back-compat).
 *
 * Le direto do localStorage em vez de via store pra evitar import
 * circular (store importa services que importam http) e funcionar mesmo
 * em request disparado antes do store estar pronto.
 *
 * NAO aplicado a httpAuth (login), httpMdfe (engine MDF-e), httpCteEngine
 * (engine CT-e) ou httpFinanceiro porque sao integracoes externas que
 * nao conhecem esse param - mandar so polui a query string.
 */
function injetarParticipanteAtivo(config) {
    try {
        const raw = localStorage.getItem('participanteAtivo')
        if (raw) {
            const p = JSON.parse(raw)
            if (p && p.id != null) {
                config.params = config.params || {}
                // Nao sobrescreve se o caller ja informou o param.
                if (config.params.idParticipanteAtivo == null) {
                    config.params.idParticipanteAtivo = p.id
                }
            }
        }
    } catch (e) {
        // localStorage indisponivel ou JSON corrompido - segue sem inject.
    }
    return config
}

/**
 * Interceptor de Authorization das instances que falam com a wi-cadastro-api.
 *
 * Ate aqui cada caller montava o header a mao, e o resultado era desigual:
 * a maior parte mandava, o Tributacao.vue nao mandava em nenhuma das ~13
 * chamadas, e duas do EmissaoViagem mandavam a string literal 'Bearer null'.
 * Funcionava porque a wi-cadastro-api termina a chain em permitAll - ou seja,
 * a API respondia sem token.
 *
 * A Fase 3 do plano de seguranca vira essa chain para
 * anyRequest().authenticated(). Sem este interceptor, tudo que hoje sai sem
 * header vira 401 no dia da virada.
 *
 * Sobrescreve o valor existente de proposito: o token do localStorage e o
 * mesmo que os callers passavam manualmente, e sobrescrever e o que neutraliza
 * um 'Bearer null' remanescente. Sem token no storage, nao inventa header -
 * quem estiver deslogado leva 401 e cai no handle401.
 *
 * Mesmo desenho do httpCiot, mais abaixo.
 */
function injetarAuthorization(config) {
    try {
        const token = localStorage.getItem('token')
        if (token) {
            config.headers = config.headers || {}
            config.headers.Authorization = 'Bearer ' + token
        }
    } catch (e) {
        // localStorage indisponivel - segue sem header.
    }
    return config
}

function attach(instance){
    instance.interceptors.response.use(undefined, handle401)
    return instance
}

/**
 * Variante de attach que tambem injeta o "Participante Ativo" no request.
 * Use SOMENTE pras instances que conversam com nossos backends que tratam
 * esse param (cte-app-web-api e wi-cadastro-api).
 */
function attachComParticipanteAtivo(instance){
    instance.interceptors.request.use(injetarParticipanteAtivo)
    instance.interceptors.response.use(undefined, handle401)
    return instance
}

export const http = attachComParticipanteAtivo(axios.create({
    baseURL: baseApiUrl
}))
// httpAuth NAO recebe o interceptor de 401 porque o endpoint de login
// retorna 401 quando o usuario erra a senha - e isso nao deve disparar
// logout (o usuario ja esta tentando logar). A tela de Login.vue trata
// esse 401 mostrando "credenciais invalidas" via seu proprio catch.
export const httpAuth = axios.create({
    baseURL: AuthApiUrl
})
export const httpMdfe = attach(axios.create({
    baseURL: MdfeApiUrl
}))
export const httpCteEngine = attach(axios.create({
    baseURL: CteEngineApiUrl
}))
export const httpCadastro = attachComParticipanteAtivo(axios.create({
    baseURL: CadastroApiUrl
}))
httpCadastro.interceptors.request.use(injetarAuthorization)

export const httpFinanceiro = attach(axios.create({
    baseURL: FinanceiroApiUrl
}))

export const httpEntregas = attach(axios.create({
    baseURL: EntregasApiUrl
}))

//
// Instancia da rotina "CIOT Avulso" (wi-ciot-api externa).
//
// Token eh per-usuario - o mesmo Bearer que ja esta no localStorage
// pra autenticar o portal. Header X-Ambiente fixo em 'producao' por
// decisao do produto (sem variacao por env build hoje).
//
// NAO usa attachComParticipanteAtivo - a wi-ciot-api eh externa e nao
// conhece o param idParticipanteAtivo. NAO injeta no nosso interceptor
// de 401 mesmo via attach() - a wi-ciot-api retorna 401 quando o JWT
// dela expira, mas como o token eh o mesmo do nosso portal, deslogar
// o usuario quando a wi-ciot rejeita esta correto.
//
// Bearer eh adicionado no proprio request interceptor abaixo (em vez
// de cada caller passar manualmente).
//
export const httpCiot = attach(axios.create({
    baseURL: WiCiotApiUrl,
    headers: { 'X-Ambiente': 'producao' }
}))
httpCiot.interceptors.request.use(function(config){
    try {
        var token = localStorage.getItem('token')
        if (token) {
            config.headers = config.headers || {}
            config.headers.Authorization = 'Bearer ' + token
        }
    } catch (e) {
        // localStorage indisponivel
    }
    return config
})

//
// Serializador de query da wi-nfse-api.
//
// O filtro de status manda uma lista ("rejeitada + falha" numa consulta
// so). O axios 0.26 serializa array como `status[]=A&status[]=B`, e o
// Spring do outro lado espera `status=A&status=B` - com o sufixo [] o
// parametro simplesmente nao casa e o filtro e ignorado em silencio.
//
// Tambem descarta chave vazia: o guia avisa que todo parametro informado
// e validado, e `cnpjTomador=` responde 400.
//
function serializarParams(params) {
    var partes = []
    Object.keys(params || {}).forEach(function(chave){
        var valor = params[chave]
        if (valor === undefined || valor === null || valor === '') return
        var lista = Array.isArray(valor) ? valor : [valor]
        lista.forEach(function(item){
            if (item === undefined || item === null || item === '') return
            partes.push(encodeURIComponent(chave) + '=' + encodeURIComponent(item))
        })
    })
    return partes.join('&')
}

//
// Instancia do Relatorio NFS-e (wi-nfse-api externa).
//
// Mesmo desenho do httpCiot: API de fora, Bearer igual ao do portal,
// sem idParticipanteAtivo (ela nao conhece o param) e com o attach() do
// 401 - o token e o mesmo, entao 401 dela e sessao expirada de verdade.
//
// O que e proprio daqui e o X-Correlation-Id: a wi-nfse-api exige o
// header, em UUID v4, em TODAS as rotas /v1/nfse/**, e sem ele responde
// 400 CORRELATION_ID_OBRIGATORIO. Um por requisicao, gerado no
// interceptor para nenhum caller precisar lembrar.
//
// baseURL pode vir vazia: sem VUE_APP_NFSE_URL no build, o item de menu
// nao e gerado e a tela avisa que a integracao nao esta configurada (ver
// config.js). Nao da para chutar a URL antes de a API ser publicada.
//
export const httpNfse = attach(axios.create({
    baseURL: NfseApiUrl,
    paramsSerializer: serializarParams
}))
httpNfse.interceptors.request.use(function(config){
    config.headers = config.headers || {}
    try {
        var token = localStorage.getItem('token')
        if (token) {
            config.headers.Authorization = 'Bearer ' + token
        }
    } catch (e) {
        // localStorage indisponivel
    }
    config.headers['X-Correlation-Id'] = novoCorrelationId()
    return config
})


